import { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { generateSocialCaptions, listCaptionBrands } from '../utils/api';
import {
  CAPTION_PLATFORMS,
  CaptionsSocialError,
  MAX_BRIEF_CHARS,
  normalizePlatform,
  pickBrandId,
} from '../utils/captionsSocial';
import { isPro, getUsageLabel } from '../utils/usage';
import { listOffers } from '../utils/vault';
import { safeCapture } from "../utils/analytics";
import { useAuth } from "../auth/AuthProvider";

const STRIPE_PRO_LINK = 'https://buy.stripe.com/6oU8wPfjX591as29Hg3Je03';

// The draft kept per user: the brief, the platform and the brand picked. Older drafts
// hold { formData: { offer, platform: "Instagram", company, ... }, step }; only offer
// and platform carry over.
function readDraft(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "null");
    const formData = parsed?.formData ?? {};
    return {
      offer: typeof formData.offer === "string" ? formData.offer : "",
      platform: formData.platform,
      brandId: typeof formData.brandId === "string" ? formData.brandId : "",
    };
  } catch {
    return { offer: "", platform: undefined, brandId: "" };
  }
}

// Captions come from POST /api/captions/social: the browser picks a brand and writes
// the brief; the worker resolves the brand's approved voice and business context and
// saves the captions before returning them. Nothing else on this screen is sent.
export default function CaptionsModule() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  const userEmail = user?.email || "";   // TEMP: for legacy utils
  const draftKey = `captionsDraft:${user?.id ?? ""}`;
  const [draft] = useState(() => readDraft(draftKey));
  const [isProUser, setIsProUser] = useState(false);
  const [usageLabel, setUsageLabel] = useState('Checking...');
  const [brands, setBrands] = useState(null); // null while loading
  const [brandsError, setBrandsError] = useState(null);
  const [brandId, setBrandId] = useState('');
  const [vaultOffers, setVaultOffers] = useState([]);
  const [brief, setBrief] = useState(draft.offer);
  const [platform, setPlatform] = useState(() => normalizePlatform(draft.platform));
  const [result, setResult] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // The brands this account may create captions for (the worker decides, by the caption
  // route's own rule), preselecting the one used last.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    listCaptionBrands()
      .then((list) => ({ list, failure: null }))
      .catch((err) => {
        console.warn("Could not load brands in CaptionsModule:", err?.code ?? "unexpected");
        return {
          list: [],
          failure: err instanceof CaptionsSocialError ? err.message : "Your brands couldn't be loaded. Please try again.",
        };
      })
      .then(({ list, failure }) => {
        if (cancelled) return;
        setBrandsError(failure);
        setBrands(list);
        setBrandId(pickBrandId(list, draft.brandId));
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, draft.brandId]);

  // Saved offers of the selected brand (they only fill in the brief).
  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    listOffers(brandId)
      .then((list) => {
        if (!cancelled) setVaultOffers(list);
      })
      .catch((err) => console.warn("Could not load offers in CaptionsModule:", err));
    return () => {
      cancelled = true;
    };
  }, [brandId]);

  // Save draft (per user), once the brand list is known. A list that failed to load
  // keeps the remembered brand rather than forgetting it.
  useEffect(() => {
    if (brands === null) return;
    const keptBrandId = brandsError ? draft.brandId : brandId;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ formData: { offer: brief, platform, brandId: keptBrandId } }));
    } catch {
      /* storage unavailable */
    }
  }, [draftKey, brands, brandsError, draft.brandId, brief, platform, brandId]);

  // Check Pro
  useEffect(() => {
    const checkUserData = async () => {
      const email = userEmail;
      if (!email || !email.includes("@")) {
        setUsageLabel("Signed in, but email missing.");
        return;
      }

      const pro = await isPro(email);
      setIsProUser(pro);
      setUsageLabel(getUsageLabel(pro));
    };

    const timer = setTimeout(checkUserData, 200);
    return () => clearTimeout(timer);
  }, [userEmail]);

  if (authLoading) {
    return (
      <div style={{ padding: 24, opacity: 0.8 }}>
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  const selectedBrand = (brands ?? []).find((b) => b.id === brandId) ?? null;

  const selectBrand = (id) => {
    setBrandId(id);
    setVaultOffers([]);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // The brand, brief and platform as they are now: the form is locked while this runs.
    const request = { brandId, brief, platform };
    const brandName = selectedBrand?.name || "your brand";
    const platformLabel = CAPTION_PLATFORMS.find((p) => p.key === platform)?.label ?? platform;

    setIsGenerating(true);

    try {
      const pro = await isPro(userEmail);
      setIsProUser(pro);
      setUsageLabel(getUsageLabel(pro));

      // The server decides the free daily limit (per account, UTC day); a request it
      // refuses comes back as free_limit_reached with its fixed message.
      const saved = await generateSocialCaptions(request);
      setResult({ ...saved, brandName, platformLabel });
    } catch (err) {
      const known = err instanceof CaptionsSocialError;

      safeCapture("caption_generation_failed", {
        is_pro: !!isProUser,
        route: window.location.pathname,
        code: known ? err.code : "unexpected",
      });

      setError(known ? err.message : "Something went wrong creating captions. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Caption copied to clipboard!');
  };

  const reset = () => {
    setResult(null);
    setError(null);
  };

  return (
  <div className="bg-white rounded-lg shadow-md p-6">
    <h2 className="text-2xl font-bold text-gray-800 mb-6">Social Media Captions</h2>

    {/* Pro Upgrade Banner - Show for non-Pro users */}
    {!isProUser && userEmail && userEmail.includes('@') && (
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              🚀 Upgrade to Pro
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Unlimited captions • All platforms • Priority support
            </p>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-3xl font-bold text-gray-900">$29</span>
              <span className="text-gray-600">/month</span>
            </div>
          </div>
          <a
            href={STRIPE_PRO_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#043C5A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#032d45] transition-colors whitespace-nowrap"
          >
            Upgrade to Pro
          </a>
        </div>
      </div>
    )}

    {/* Brief */}
    {!result && (
      <form onSubmit={handleSubmit}>
        <fieldset disabled={isGenerating} className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-sm text-gray-700">
              Signed in as <span className="font-medium">{userEmail || "—"}</span>
            </p>
            {userEmail && (
              <p className="mt-1 text-xs text-gray-500">{usageLabel}</p>
            )}
          </div>

          <div>
            <label htmlFor="caption-brand" className="block text-sm font-medium text-gray-700 mb-1">
              Brand *
            </label>
            {brands === null ? (
              <p className="text-sm text-gray-500">Loading your brands…</p>
            ) : brandsError ? (
              <p className="text-sm text-red-800">{brandsError}</p>
            ) : brands.length === 0 ? (
              <p className="text-sm text-gray-700">
                You don&apos;t have a brand you can create captions for yet.
              </p>
            ) : (
              <select
                id="caption-brand"
                value={brandId}
                onChange={(e) => selectBrand(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sabre-blue focus:border-transparent"
              >
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name || "Untitled brand"}</option>
                ))}
              </select>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Captions are written in this brand&apos;s voice, from its approved business details.
            </p>
          </div>

          {vaultOffers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Choose from your saved offers (optional)
              </label>
              <select
                value=""
                onChange={(e) => {
                  const picked = vaultOffers.find((o) => o.id === e.target.value);
                  if (picked) setBrief(String(picked.name ?? "").slice(0, MAX_BRIEF_CHARS));
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sabre-blue focus:border-transparent"
              >
                <option value="">— Select a saved offer —</option>
                {vaultOffers.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="caption-brief" className="block text-sm font-medium text-gray-700 mb-1">
              What are you promoting? *
            </label>
            <input
              id="caption-brief"
              type="text"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              maxLength={MAX_BRIEF_CHARS}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sabre-blue focus:border-transparent"
              placeholder="e.g., Free 30-minute consultation"
              required
            />
          </div>

          <div>
            <label htmlFor="caption-platform" className="block text-sm font-medium text-gray-700 mb-1">
              Platform *
            </label>
            <select
              id="caption-platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sabre-blue focus:border-transparent"
            >
              {CAPTION_PLATFORMS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Error display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isGenerating || !selectedBrand}
            className="w-full bg-sabre-blue text-white py-3 rounded-lg font-medium hover:bg-sabre-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? 'Generating...' : 'Create Captions'}
          </button>
        </fieldset>
      </form>
    )}

    {/* Results: shown only once the worker has confirmed the save */}
    {result && (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            Your captions for {result.brandName} · {result.platformLabel}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Saved to caption history (ref {result.historyId.slice(0, 8)})
          </p>
        </div>

        {result.captions.map((caption, index) => (
          <div key={caption.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-start mb-2">
              <span className="text-sm font-medium text-gray-600">
                Option {index + 1}
              </span>
              <button
                onClick={() => copyToClipboard(caption.text)}
                className="text-sm text-slate-900 hover:text-slate-700 font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-gray-800 whitespace-pre-wrap">
              {caption.text}
            </p>
          </div>
        ))}

        <button
          onClick={reset}
          className="w-full bg-sabre-blue text-white py-3 rounded-lg font-medium hover:bg-sabre-blue/90 transition-colors"
        >
          Start Over
        </button>
      </div>
    )}
  </div>
  );
}
